import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { useAuth } from "../AuthContext";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

const empty = { name: "", legal_name: "", gstin: "", address1: "", address2: "", city: "", pincode: "", state: "", state_code: "", is_own_branch: false, platform: "" };
const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0047AB]/20 focus:border-[#0047AB]";
const labelCls = "text-xs font-semibold uppercase tracking-wider text-slate-600";

export default function AddressBook() {
  const { user } = useAuth();
  const [parties, setParties] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const canEdit = user.role !== "checker";

  const load = () => api.get("/parties").then((r) => setParties(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/parties/${editId}`, form);
      else await api.post("/parties", form);
      toast.success(editId ? "Party updated" : "Party added");
      setOpen(false); setForm(empty); setEditId(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/parties/${id}`); toast.success("Party deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const fields = [
    ["name", "Display Name *", true], ["legal_name", "Legal Name", false], ["gstin", "GSTIN", false],
    ["address1", "Address Line 1 *", true], ["address2", "Address Line 2", false], ["city", "City *", true],
    ["pincode", "Pincode *", true], ["state", "State *", true], ["state_code", "State Code (e.g. 09) *", true],
  ];

  return (
    <div data-testid="parties-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Masters</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>Address Book</h1>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditId(null); } }}>
            <DialogTrigger asChild>
              <button data-testid="add-party-btn" className="inline-flex items-center gap-2 bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200">
                <Plus size={16} /> Add Party
              </button>
            </DialogTrigger>
            <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editId ? "Edit Party" : "Add Party"}</DialogTitle></DialogHeader>
              <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
                {fields.map(([k, label, req]) => (
                  <div key={k} className={k.startsWith("address") ? "sm:col-span-2" : ""}>
                    <label className={labelCls}>{label}</label>
                    <input data-testid={`party-${k}-input`} required={req} value={form[k]}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={`${inputCls} mt-1`} />
                  </div>
                ))}
                <div>
                  <label className={labelCls}>Platform tag</label>
                  <select data-testid="party-platform-select" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className={`${inputCls} mt-1`}>
                    <option value="">None</option>
                    <option value="amazon">Amazon</option><option value="flipkart">Flipkart</option>
                    <option value="blinkit">Blinkit</option><option value="firstcry">Firstcry</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 text-sm mt-5">
                  <input data-testid="party-own-branch-checkbox" type="checkbox" checked={form.is_own_branch}
                    onChange={(e) => setForm({ ...form, is_own_branch: e.target.checked })} />
                  Own Branch (Bill-to entity)
                </label>
                <div className="sm:col-span-2 flex justify-end">
                  <button data-testid="party-save-btn" className="bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200">Save</button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {parties.map((p) => (
          <div key={p.id} data-testid={`party-card-${p.id}`} className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm transition-shadow duration-200">
            <div className="flex items-start justify-between gap-2">
              <div className="font-semibold text-slate-900">{p.name}</div>
              {canEdit && (
                <div className="flex gap-2 shrink-0">
                  <button data-testid={`party-edit-${p.id}`} onClick={() => { setForm({ ...empty, ...p }); setEditId(p.id); setOpen(true); }}
                    className="text-slate-400 hover:text-[#0047AB] transition-colors duration-200"><Pencil size={14} /></button>
                  <button data-testid={`party-delete-${p.id}`} onClick={() => remove(p.id)}
                    className="text-slate-400 hover:text-red-600 transition-colors duration-200"><Trash2 size={14} /></button>
                </div>
              )}
            </div>
            <div className="text-sm text-slate-600 mt-1">{p.address1}{p.address2 ? `, ${p.address2}` : ""}</div>
            <div className="text-sm text-slate-600">{p.city} - {p.pincode}, {p.state} ({p.state_code})</div>
            {p.gstin && <div className="text-xs font-mono text-slate-700 mt-1.5">GSTIN: {p.gstin}</div>}
            <div className="flex gap-2 mt-2">
              {p.is_own_branch && <span className="text-[10px] uppercase tracking-wide bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-2 py-0.5 font-semibold">Own Branch</span>}
              {p.platform && <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 border border-slate-200 rounded-full px-2 py-0.5 font-semibold">{p.platform}</span>}
            </div>
          </div>
        ))}
        {parties.length === 0 && <div className="text-sm text-slate-400">No parties saved yet.</div>}
      </div>
    </div>
  );
}
